/**
 * @file UnitTree.tsx
 * © EMETL Working Group
 * 
 * This component represents a tree to set rank
 *
 * @author Andrew Berger
 */

import React, { 
    useState, 
    useEffect,
    useContext,
} from "react"

import {
    Diagram3, 
} from "react-bootstrap-icons"

import { 
    Col, 
    Button, 
    Modal,
    Row, 
    Spinner, 
    Form 
} from "react-bootstrap"

import Badge from "react-bootstrap/Badge"

import {
    Context,
} from "../Model/Contexts/AppContext"

import UnitTreeVM from "../ViewModel/UnitTree.vm"
import EMETLDynamicModal from "./Components/EMETLDynamicModal"
import EMETLUnitTreeColumn from "./Components/EMETLUnitTreeColumn"
import EMETLContextMenu from "./Components/EMETLContextMenu"
import EMETLUnitTreeSearchHeader from "./Components/Header/EMETLUnitTreeSearchHeader"

import { 
    ChevronLeft 
} from "react-bootstrap-icons"

import FullTree from "./FullTree"

import { 
    IS_DEV 
} from "../ViewModel/devUtils"

import "./Stylesheets/UnitTree.css"
import "./Stylesheets/App.css"

let selectedDepth = 0
let leftColumnDataHardCopy = []

const UnitTree = () => {
    UnitTreeVM.ctx = useContext(Context)

    const {
        header: {
            update: setHeader,
        }
    } = UnitTreeVM.ctx

    const [leftColumnData, setLeftColumnData] = useState([])
    const [rightColumnData, setRightColumnData] = useState([])
    const [allVisibleTreeData, setAllVisibleTreeData] = useState({})
    const [selectedLeftIndex, selectLeftIndex] = useState(undefined)
    const [selectedRightIndex, selectRightIndex] = useState(undefined)
    const [contextUnitID, setContextUnitID] = useState(null)
    const [contextX, setContextX] = useState(null)
    const [contextY, setContextY] = useState(null)
    const [showContext, setShowContext] = useState(false)
    const [fullTreeOpen, setFullTreeOpen] = useState(false)
    const [loading, setLoading] = useState(false)
    const [parentUnit, setParentUnit] = useState(undefined)
    const [showModal, setShowModal] = useState(undefined)
    const [modalContent, setModalContent] = useState(null)
    const [rootUnitName, setRootUnitName] = useState("")
    UnitTreeVM.unitTreeViewLoading = setLoading
    const setTree = (treeData, lhs = null) => {
        const [leftData, rightData] = UnitTreeVM.splitByColumn(treeData, selectedDepth)
        setLeftColumnData(leftData)
        leftColumnDataHardCopy = leftData
        setRightColumnData(rightData.filter(unit => unit.parentUnitID === lhs))
        setAllVisibleTreeData(treeData)
        setLoading(false)
    }

    const loadRoot = () => {
        UnitTreeVM.loadUnits(0, null, processUnitTreeData)
    }

    const processUnitTreeData = (data, lhs = null) => {
        const processedData = UnitTreeVM.processUnitTreeData(data)
        setTree(processedData, lhs)
    }
    
    const handleSelectionLeft = (idx: number) => {
        selectRightIndex(undefined)
        selectLeftIndex(idx)
        setTree(allVisibleTreeData, leftColumnData[idx].unitID)
    }

    const handleSelectionRight = (idx: number) => {
        selectRightIndex(idx)
        selectLeftIndex(undefined)
        selectedDepth++
        if (selectedDepth > 1) {
            setLoading(true)
            const parent = allVisibleTreeData[rightColumnData[idx].parentUnitID]
            setParentUnit(parent)
            UnitTreeVM.loadUnits(selectedDepth, rightColumnData[idx].parentUnitID, (data) => {
                processUnitTreeData(data, rightColumnData[idx].unitID)
                selectRightIndex(undefined)
                selectLeftIndex(idx)
                setLoading(false)
            })
        }
        else if (selectedDepth === 1) {
            setTree(allVisibleTreeData, rightColumnData[idx].unitID)
            selectRightIndex(undefined)
            selectLeftIndex(idx)
        }
    }

    const handleBackButtonPress = async () => {
        setLoading(true)

        const grandparent = parentUnit.parentUnitID 
            ? await UnitTreeVM.loadUnit(parentUnit.parentUnitID) 
            : undefined

        setParentUnit(grandparent)
        selectedDepth--
        UnitTreeVM.loadUnits(parentUnit.parentUnitID ? selectedDepth : 0, parentUnit.parentUnitID, (data) => {
            processUnitTreeData(data, parentUnit.unitID)
            leftColumnDataHardCopy.forEach(
                (u, idx) => {
                    if (u.unitID === parentUnit.unitID) {
                        selectLeftIndex(idx)
                        selectRightIndex(undefined)
                    }
                }
            )
            setLoading(false)
        })
    }

    const handleContextOpen = (x, y, unitID) => {
        setContextX(x)
        setContextY(y)
        setContextUnitID(unitID)
        setShowContext(true)
    }

    const handleContextClose = () => {
        setShowContext(false)
    }

    const selectUnit = async (selectedUnit) => {
        const parent = selectedUnit.ParentUnitID 
            ? await UnitTreeVM.loadUnit(selectedUnit.ParentUnitID) 
            : undefined
        setParentUnit(parent)
        selectedDepth = selectedUnit.Depth
        UnitTreeVM.loadUnits(parent ? selectedDepth : 0, selectedUnit.ParentUnitID, (d) => {
            processUnitTreeData(d, selectedUnit.ID)
            leftColumnDataHardCopy.forEach(
                (u, idx) => {
                    if (u.unitID === selectedUnit.ID) {
                        selectLeftIndex(idx)
                        selectRightIndex(undefined)
                    }
                }
            )
            setLoading(false)
        })
    }

    const selectSearchResult = async () => {
        selectUnit(null)
    }
    
    const refreshUnitTreeUI = () => {
        
        const selectedUnit = leftColumnData[selectedLeftIndex]
        if (selectedUnit) {
            selectUnit({
                ID: selectedUnit?.unitID,
                Depth: selectedUnit?.depth,
                ParentUnitID: selectedUnit?.parentUnitID,
            })
        } else {
            loadRoot()
        }
    }

    const handleShowModal = (content) => {
        setModalContent(content)
        setShowModal(true)
    }

    const handleModalSave = () => {
        refreshUnitTreeUI()
        setShowModal(false)
    }

    const createRootUnit = () => {
        UnitTreeVM.addUnit({
            unit: {
                unitName: rootUnitName,
                unitID: -1, 
            }, 
            unitName: rootUnitName,
        }, handleShowModal)
    }

    const removeUser = async (user) => {
        const confirmedRemoval = window.confirm(`Are you sure you want to remove ${user.Name} from this unit?`)
        if (!confirmedRemoval) 
            return

        const branchID = user["@b"]
        
        const res = await UnitTreeVM.removeUserFromBranch(user.ID, branchID)
        if (res?.BranchID === branchID && res?.UserID === user.ID) {
            alert(`${user.Name} has been removed from this unit.`)
            handleModalSave()
        } else {
            alert(`There was an error removing ${user.Name} from this unit. Please try again later.`)
        }
    }

    useEffect(() => {
        setLoading(true)
        setHeader((
            <EMETLUnitTreeSearchHeader 
                onSelect={selectUnit}
            />
        ))

        // if (!!Object.keys(data).length) {
        //     selectSearchResult()
        // } else {
        //     loadRoot()
        // }
        loadRoot()
    }, [])

    if(fullTreeOpen) {
        return (
            <FullTree close={() => setFullTreeOpen(false)} />
        )
    }

    return (
        <>
            <Modal
                show={showModal}
                onHide={()=>setShowModal(false)}
                size="xl"
            >
                <Modal.Header>
                    <Modal.Title>
                        {modalContent?.title}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <EMETLDynamicModal
                        content={{
                            ...modalContent,
                            onSave: handleModalSave,
                            onCancel: () => setShowModal(false),
                            removeUser,
                        }}
                    />
                </Modal.Body>
            </Modal>
            {parentUnit && (
                <Button className="col-2" variant="link" onClick={handleBackButtonPress} style={{ alignContent: "center" }}>
                    <ChevronLeft />
                    {parentUnit.name}
                </Button>
            )
            }
            {loading ? <div className="spinnerBox"><Spinner animation="border" role="status" /></div> : (
                <>
                    {selectedDepth < 2 && (
                        <div id="createRootUnitAction">
                            <Form id="createRootUnitActionForm">
                                <Row>
                                    <Form.Group as={Col}>
                                        <Form.Control
                                            onChange={(e) => setRootUnitName(e.target.value)}
                                            value={rootUnitName}
                                            placeholder="Root Unit Name"
                                        />
                                    </Form.Group>
                                    <Form.Group 
                                        as={Col}
                                    >
                                        <Button
                                            // as={Col}
                                            onClick={createRootUnit}
                                            variant="outline-dark"
                                        >
                                            Create Root Unit
                                        </Button>
                                    </Form.Group>
                                </Row>
                            </Form>
                        </div>
                    )}
                    <div className="UnitTree" id="UnitTreeParent">
                        <div className="container w-100">
                            <div className="row mt-3">
                                <EMETLUnitTreeColumn
                                    data={leftColumnData}
                                    selectedIndex={selectedLeftIndex}
                                    onSelect={handleSelectionLeft} 
                                    onContextOpen={handleContextOpen}
                                    createRoot={createRootUnit}
                                />
                                <div className="col-sm" />
                                <EMETLUnitTreeColumn
                                    data={rightColumnData}
                                    selectedIndex={selectedRightIndex}
                                    onSelect={handleSelectionRight} 
                                    onContextOpen={handleContextOpen}
                                    createRoot={createRootUnit}
                                />
                                
                            </div>
                        </div>
                        
                        {showContext && (
                            <EMETLContextMenu 
                                name={allVisibleTreeData[contextUnitID].name} 
                                onCloseMenu={handleContextClose}
                                xPos={contextX}
                                yPos={contextY}
                                actions={
                                    (u) => {
                                        return UnitTreeVM.partialUnitTreeViewContextMenuActions(
                                            u, 
                                            handleShowModal,
                                            refreshUnitTreeUI,
                                        )
                                    }
                                }
                                unit={allVisibleTreeData[contextUnitID]}
                            />
                        )}
                    </div>
                </>

            )}
            {(IS_DEV || true) && // temporarily enable the full tree in prod
                <Row id="fullTreeButtonRow">
                    <Button 
                        onClick={() => setFullTreeOpen(true)}
                        variant="outline-dark"
                    >
                        <Diagram3 />
                        <span className="m-2">Full Tree</span>
                        <Badge bg="secondary">beta</Badge>
                    </Button>
                </Row>
            }
        </>
    )
}

export default UnitTree
